import axios from 'axios';
import { IMessage, IMessages, IHistory, THistory, FileUpload, IChat } from '../interface/IModels';
import { ChatItemModel, CompanyItem, DepartmentItem, MessageModel } from '../models/redux-models';
import toast from 'react-hot-toast';

interface createMessageAgentOptions {
  isDirect?: boolean;
  department_id?: number;
  webhookUrl?: string;
}

const baseUrl: string = process.env.REACT_APP_URL_APP_BOT_API + 'chat/' || "";

const chatService = {

  async getMessagesByNumber(phone: string, id: number): Promise<IMessages> {
    const tokenJWT = localStorage.getItem("auth");
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${tokenJWT}`,
    };

    let messages: IMessage[] = [];

    try {
      let url = baseUrl + "getChats.php";
      const response = await axios.post<IMessage[]>(url, { "phone": phone }, { headers });
      messages = response.data;

      axios.post(baseUrl + "readMessages.php", { chat_id: id }, { headers })
           .catch(err => console.error("Erro ao chamar readMessages.php em getMessagesByNumber:", err));

      return messages;
    } catch (err) {
      console.error("Erro em getMessagesByNumber:", err);
      return [];
    }
  },

  async createMessageAgent(
    message: IMessage,
    phone: string,
    fileToUpload?: FileUpload,
    options?: createMessageAgentOptions
  ): Promise<number> {
    const tokenJWT = localStorage.getItem("auth");
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${tokenJWT}`,
    };
    const url = baseUrl + "sendMessageAgentV2.php";

    try {
      const response = await axios.post<number>(
        url,
        {
          ...message,
          department: options?.department_id ?? 0,
          phone: phone,
          file: fileToUpload,
          isDirect: options?.isDirect ?? false,
          webhookUrl: options?.webhookUrl ?? ""
        },
        { headers }
      );

      axios.post(baseUrl + "readMessages.php", { chat_id: message.chat_id }, { headers })
        .catch(err => console.error("Erro ao ler mensagens após envio:", err));

      return response.data;
    } catch (err) {
      console.error("Falha ao mandar mensagem:", err);
      toast.error("Falha ao mandar mensagem, envie-a novamente!", { duration: 2000 });
      return 0;
    }
  },

  async createChatByApp(chatDetails: IChat, photoUrl: string): Promise<{ exists: boolean, validPhone: number, id: number }> {
    const tokenJWT = localStorage.getItem("auth");
    const url = baseUrl + "createChatV3.php";

    let clientToken = '';
    if (chatDetails.companyId != 22) clientToken = 'F86e6d40befa14914aa4077c8986569b6S';
    else clientToken = 'Fe113fc1e2fac430f93ff05d20680d8caS';

    const headers = {
      'Content-Type': 'application/json',
      'Client-Token': clientToken,
    };

    try {
      const photoResponse = await axios.get(photoUrl, { headers });
      let photo;
      if (!photoResponse.data) {
        photo = "https://gravatar.com/avatar/29967191/e9c43271a321f0af66f5da2e4462cb8b.jpeg?size=2500";
      } else {
        photo = photoResponse.data;
      }

      const headersApp = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenJWT}`,
      };

      const createChatResponse = await axios.post<{ exists: boolean, validPhone: number, id: number }>(
        url,
        {
          phone: chatDetails.phone,
          name: chatDetails.name,
          company_id: chatDetails.companyId,
          department: chatDetails.departmentId,
          picture: photo
        },
        { headers: headersApp }
      );

      return createChatResponse.data;
    } catch (err: any) {
      console.error("Erro ao criar chat pelo aplicativo:", err);
      return { exists: false, validPhone: 0, id: 0 };
    }
  },

  async getChatActive(chat_id: number): Promise<ChatItemModel> {
    const tokenJWT = localStorage.getItem("auth");
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${tokenJWT}`,
    };

    let chatData: ChatItemModel;

    try {
      const chatResponse = await axios.post<ChatItemModel>(
        baseUrl + "getChatV2.php",
        { chat_id: chat_id },
        { headers }
      );
      chatData = chatResponse.data;

      try {
        const historyResponse = await axios.post<IHistory[]>(
          baseUrl + "getChatsArchivedV2.php",
          { phone: chatData.phone, company: chatData.companyID },
          { headers }
        );

        const history: THistory = {
          history: true,
          historys: historyResponse.data || [] 
        };
        chatData.history = history; 
      } catch (historyError) {
        console.warn("DEBUG: chatService.getChatActive - Erro ao buscar histórico de chat, inicializando history:", historyError);
        chatData.history = chatData.history || { history: false, historys: [] };
      }

      axios.post(
        baseUrl + "readMessages.php",
        { chat_id: chatData.id },
        { headers }
      ).catch(error => console.warn("DEBUG: chatService.getChatActive - Erro ao marcar mensagens como lidas:", error));

      return chatData;
    } catch (error) {
      return {
        id: 0, agent: null, agent_id: null, name: "", phone: "", picture: "",
        messages: 0, messages_read: 0, updatedAt: "", isGroup: false, companyID: 0,
        departmentID: 0, messages_chat: [], extra_payload: "", isMessagesLoading: false,
        history: { history: false, historys: [] }
      };
    }
  },

  async getChatsInAttendance(companys: CompanyItem[], departments: DepartmentItem[]): Promise<ChatItemModel[]> {
    const tokenJWT = localStorage.getItem("auth");
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${tokenJWT}`,
    };

    const url = baseUrl + "getChatsInAttendance.php";
    const companyIDs = companys.map(e => e.id);
    const departmentIDs = departments.map(e => e.id);

    if (!companys.length) return [];

    try {
      const response = await axios.post<ChatItemModel[]>(
        url,
        { companys: companyIDs, departments: departmentIDs },
        { headers }
      );

      return response.data.map(chat => ({
        ...chat,
        history: chat.history || { history: false, historys: [] },
        messages_chat: chat.messages_chat || []
      }));
    } catch (err) {
      console.error("Erro ao buscar chats em atendimento:", err);
      return [];
    }
  },

  async closeChat(chat_id: number, webhook_id?: string, ticket_number?: string, data?: any | null): Promise<boolean> {
    const tokenJWT = localStorage.getItem("auth");
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tokenJWT}`, };
    const url = baseUrl + "closeChatV2.php";
    try {
      const response = await axios.post<boolean>(url, { id: chat_id, webhook_id: webhook_id, ticket_number, data }, { headers });
      return response.data;
    } catch (err) {
      console.error("Erro ao fechar chat:", err);
      return false;
    }
  },

  async getCustomerExtraInformationByExtraPayload(phone: string, id_company: number): Promise<any | boolean> {
    const tokenJWT = localStorage.getItem("auth");
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tokenJWT}`, };
    const url = baseUrl + "getCustomerExtraInformation.php";
    try {
      const response = await axios.post<any>(url, { phone, id_company }, { headers });
      return response.data;
    } catch (err) {
      console.error("Erro ao obter informações extras do cliente:", err);
      return false;
    }
  },

  async getChatDetails(chat_id: number, phone: string) {
    type GetChatDetailsResponse = { archived: boolean; pinnd: boolean; messagesUnread: number; phone: string; unread: number; name: string; lastMessageTime: string; muteEndTime: string; isMuted: string; isMarkedSpam: string; time: string; };
    const tokenJWT = localStorage.getItem("auth");
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tokenJWT}`, };
    const url = baseUrl + "getChatDetails.php";
    try {
      const response = await axios.post<GetChatDetailsResponse>(url, { id: chat_id, phone: phone }, { headers });
      return response.data;
    } catch (err) {
      console.error("Erro ao obter detalhes do chat:", err);
      return false;
    }
  },

  async getMessageByID(message_id: number): Promise<IMessage | boolean> {
    const tokenJWT = localStorage.getItem("auth");
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tokenJWT}`, };
    const url = baseUrl + "getMessage.php";
    try {
      const response = await axios.post<IMessage>(url, { id: message_id }, { headers });
      return response.data;
    } catch (err) {
      console.error("Erro ao obter mensagem por ID:", err);
      return false;
    }
  },

  async readMessages(chat_id: number): Promise<boolean> {
    const tokenJWT = localStorage.getItem("auth");
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tokenJWT}`, };
    const url = baseUrl + "readMessages.php";
    try {
      await axios.post(url, { chat_id: chat_id }, { headers });
      return true;
    } catch (error) {
      console.error("Erro ao marcar mensagens como lidas:", error);
      return false;
    }
  },

  async getChatMetadata(company_id: number, phone: string): Promise<any | boolean> {
    const tokenJWT = localStorage.getItem("auth");
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tokenJWT}`, };
    const url = baseUrl + "getChatMetadata.php";
    try {
      const response = await axios.post(url, { company_id: company_id, phone: phone }, { headers });
      return response.data;
    } catch (error) {
      console.error("Erro ao obter metadados do chat:", error);
      return false;
    }
  },

  async updateChatDepartament(chat_id: number, departament_id: number): Promise<boolean> {
    const tokenJWT = localStorage.getItem("auth");
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tokenJWT}`, };
    const url = baseUrl + "updateDepartment.php";
    try {
      const response = await axios.post(url, { id_chat: chat_id, id_departament: departament_id }, { headers });
      return response.data;
    } catch (error) {
      console.error("Erro ao atualizar departamento do chat:", error);
      return false;
    }
  },

  async setChatAttendant(chatId: number, userId: number): Promise<any> {
    const tokenJWT = localStorage.getItem("auth");
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tokenJWT}`, };
    const url = baseUrl + 'setAgentWithSocket.php';
    try {
      const request = await axios.post(url, { chat_id: chatId, agent_id: userId }, { headers });
      return request.data;
    } catch (err) {
      console.error("Erro ao definir atendente do chat:", err);
      throw err;
    }
  },

  async getChatByNumber(phone: string, companyId: string): Promise<any | boolean> {
    const tokenJWT = localStorage.getItem("auth");
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tokenJWT}`, };
    const url = baseUrl + "findChatByPhoneAndCompany.php";
    try {
      const response = await axios.post(url, { phone: phone, companyID: companyId }, { headers });
      return response.data;
    } catch (error) {
      console.error("Erro ao buscar chat por número:", error);
      return false;
    }
  },

  async listContacts(nameOrPhone: string, companyId: number[]): Promise<{ name: string; phone: string }[]> {
    const url = process.env.REACT_APP_URL_APP_BOT_API + `client/search.php?search=${nameOrPhone}${companyId.map((c) => `&company[]=${c}`).join("")}`;
    try {
      const response = await axios.get(url);
      return response.data as { name: string; phone: string }[];
    } catch (error) {
      console.error("Erro ao listar contatos:", error);
      return [];
    }
  },

  async verifyIfChatAlreadyOpen(originalPhone: string, companyId: number, possiblePhones: string[]): Promise<boolean> {
    const JWTToken = localStorage.getItem("auth");
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${JWTToken}` };
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_URL_APP_BOT_API}/chat/statusChatV2.php`,
        { companyID: companyId, originalPhone: originalPhone, phones: possiblePhones },
        { headers }
      );
      console.log(response.data.id);
      return !!(response.data.id ?? false);
    } catch (error) {
      console.error("Erro ao verificar se o chat já está aberto:", error);
      return false;
    }
  },

  async updateNameUserChat(name: string, phone: string, idCompany: number, idAgent: string): Promise<boolean> {
    const chatBaseUrl: string = process.env.REACT_APP_URL_APP_BOT_API + 'client/' || "";
    const tokenJWT = localStorage.getItem("auth");
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tokenJWT}`, };
    const urlRequest = chatBaseUrl + "updateNameUserChat.php";
    try {
      const response = await axios.post<any>(urlRequest, { name: name, phone: phone, idCompany: idCompany, idAgent: idAgent }, { headers });
      return response.data > 0;
    } catch (error) {
      console.error("Erro ao atualizar nome do usuário do chat:", error);
      return false;
    }
  },

  async transferAttendanceByAgentOrDepartment(idChat: number, idDepartment: string, idUser: string): Promise<boolean> {
    const tokenJWT = localStorage.getItem("auth");
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tokenJWT}`, };
    const url = baseUrl + "transferAttendance.php";
    try {
      const response = await axios.post<any>(url, { idChat, idDepartment, idUser }, { headers });
      console.log(response.status == 200);
      return response.status == 200;
    } catch (error) {
      console.error("Erro ao transferir atendimento:", error);
      return false;
    }
  }
};

export default chatService;